import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography, CircularProgress, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import AppLayout from "../../components/AppLayout";
import QRGenerator from "../../components/PdfQrcodeLayout/QrCodeGeneration";
import PdfRomaneioVendas from "../../components/PdfQrcodeLayout/pdfQrcodeComponents/PdfRomaneioVendas";
import { VendaService } from "../../service/venda_service";
import { useUser } from "../../context/UserContext";

/** Portal de rastreabilidade. Mesmo endereço que a tela de etiquetas usa. */
const baseDoPortal = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

const ID_QRCODE_VENDA = "romaneio-qrcode-venda";

function RomaneioVendas() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { user } = useUser();

	const [venda, setVenda] = useState(null);
	const [loading, setLoading] = useState(true);
	const [erro, setErro] = useState(null);
	// Os canvases de QR Code precisam existir no DOM antes do PDF ser montado:
	// o documento lê cada um com getElementById().toDataURL().
	const [qrCodesProntos, setQrCodesProntos] = useState(false);

	const carregarVenda = useCallback(async () => {
		setLoading(true);
		setErro(null);
		try {
			const vendaService = new VendaService();
			const dados = await vendaService.getById(id);
			setVenda(dados);
		} catch (error) {
			console.error("Erro ao carregar venda:", error);
			setErro(error.message || "Erro ao carregar a venda.");
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		if (id) carregarVenda();
	}, [id, carregarVenda]);

	// Um tick depois de renderizar os canvases escondidos eles já estão no DOM.
	useEffect(() => {
		if (!venda) return;
		const timer = setTimeout(() => setQrCodesProntos(true), 0);
		return () => clearTimeout(timer);
	}, [venda]);

	const itens = venda?.itens ?? [];

	// Um único parâmetro com o código de rastreio inteiro, como no portal do
	// campo. O código é composto (venda + lote), mas continua sendo um só.
	const linkDoPortal = (codigo) =>
		`${baseDoPortal}/rastreabilidade?id=${encodeURIComponent(codigo)}`;

	const codigoDoItem = (item) =>
		`${venda?.numeroVenda}-${
			item.ordemProducao?.loteRastreabilidade || item.ordemProducao?.numeroOP
		}`;

	const idsQrCodeItens = itens.reduce((mapa, item) => {
		mapa[item.id] = `romaneio-qrcode-item-${item.id}`;
		return mapa;
	}, {});

	const documento = (
		<PdfRomaneioVendas
			venda={venda}
			usuario={user}
			idQrCodeVenda={ID_QRCODE_VENDA}
			idsQrCodeItens={idsQrCodeItens}
		/>
	);

	const nomeDoArquivo = `Romaneio de vendas${
		venda?.numeroVenda ? ` - ${venda.numeroVenda}` : ""
	}.pdf`;

	return (
		<AppLayout currentPage="vendas">
			<Box sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 3,
						gap: 2,
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<IconButton onClick={() => navigate("/vendas")} sx={{ mr: 1 }}>
							<ArrowBackIcon />
						</IconButton>
						<Box>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								Romaneio de vendas
							</Typography>
							{venda?.numeroVenda && (
								<Typography variant="body2" sx={{ color: "#666" }}>
									Venda {venda.numeroVenda}
									{venda.cliente?.nome ? ` — ${venda.cliente.nome}` : ""}
								</Typography>
							)}
						</Box>
					</Box>

					{venda && qrCodesProntos && (
						<PDFDownloadLink
							document={documento}
							fileName={nomeDoArquivo}
							style={{ textDecoration: "none" }}
						>
							{({ loading: gerando }) => (
								<Button
									variant="contained"
									disabled={gerando}
									sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#158374" } }}
								>
									{gerando ? "Gerando documento..." : "Baixar PDF"}
								</Button>
							)}
						</PDFDownloadLink>
					)}
				</Box>

				{/* Canvases de QR Code: ficam escondidos, o PDF os converte em imagem. */}
				<Box sx={{ display: "none" }}>
					{venda && (
						<QRGenerator
							value={linkDoPortal(String(venda.numeroVenda))}
							documentId={ID_QRCODE_VENDA}
							margin
						/>
					)}
					{itens.map((item) => (
						<QRGenerator
							key={item.id}
							value={linkDoPortal(codigoDoItem(item))}
							documentId={idsQrCodeItens[item.id]}
							margin
						/>
					))}
				</Box>

				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
						<CircularProgress sx={{ color: "#1a9e8f" }} />
					</Box>
				)}

				{!loading && erro && (
					<Typography sx={{ color: "#ff4d4d" }}>{erro}</Typography>
				)}

				{!loading && !erro && venda && itens.length === 0 && (
					<Typography sx={{ color: "#666" }}>
						Esta venda não tem itens, então não há romaneio a gerar.
					</Typography>
				)}

				{!loading && !erro && venda && itens.length > 0 && qrCodesProntos && (
					<Box sx={{ height: "75vh" }}>
						<PDFViewer
							style={{
								width: "100%",
								height: "100%",
								border: "none",
								backgroundColor: "#525659",
							}}
							showToolbar={false}
						>
							{documento}
						</PDFViewer>
					</Box>
				)}
			</Box>
		</AppLayout>
	);
}

export default RomaneioVendas;
